import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DocumentLetterType } from "@app/core/model/document-letter-type.model";
import { SharedDocumentsService } from "@app/shared/services/shared-documents.service";
import { ShellService } from "@mynexus/mfe-core";
import { ProductNames, TableColumn, TenantNames } from "@mynexus/mynexus-ui-lib";
import { Observable, map, of } from "rxjs";
import { DocumentType } from "../models/document-type.enum";
import { ServiceLineStatuses } from "@app/products/pac/models/service-lines-status.enum";
import { FaxDocumentType } from "../models/fax-document-type.interface";
import { ICoreAuthorizationDTO } from "@app/core/model/core-authorization.model";
import { IServicingProviderDTO } from "@app/core/model";
import { DocumentTypesList } from "@app/documents/models/document-types";

const TYPES_ANTHEM_REGENCE = [
    DocumentType.Approval,
    DocumentType.DenialLetter,
    DocumentType.DENC,
    DocumentType.Dismissal,
    DocumentType.ExpeditedGrievance,
    DocumentType.SPT,
];

const TYPES_AETNA = [
    DocumentType.Approval,
    DocumentType.DenialLetter,
    DocumentType.DENC,
    DocumentType.Dismissal,
    DocumentType.DismissalNoAor,
    DocumentType.ExpeditedGrievance,
    DocumentType.ReopenedApproval,
    DocumentType.SPT,
    DocumentType.DecisionTimeframeExtension
];

const noAOR = 'Invalid Request- Unauthorized requestor (no AOR)';

@Injectable({
    providedIn: 'root',
  })
  export class DocumentsUploadLetterHHService extends SharedDocumentsService {
    constructor(
      http: HttpClient,
      shellService: ShellService,
    ) {
        super(http, shellService, ProductNames.HomeHealth);
    }

  isSingleLineSelection(letterType?: string): boolean {
    if (letterType === DocumentTypesList.DecisionTimeframeExtensionLetter)
      return true;
    else
      return false;
  }

    getLetterTypes(tenantId: string, authorizationByProductId: string, tenantName: TenantNames): Observable<DocumentLetterType[]> {
      return this.http.get<FaxDocumentType[]>(`${this.API_URL}/Utilities/FaxDocumentTypes`)
        .pipe(
          map(res => this.mapDocumentLetterTypes(res, tenantName)),
          map(res => res.map(el => {
            return {
              documentDisplayName: el.documentDisplayName,
              documentType: el.documentType,
              // todo: for other streams relies on backend
              enableLineSelection: ![
                DocumentType.DENC,
                DocumentType.SPT,
              ].includes(el.documentType),
              isEligibleForUpload: true,
            };
          })),
        )
    }

    private mapDocumentLetterTypes(documentTypes: FaxDocumentType[], tenantName: TenantNames): FaxDocumentType[] {
        switch (tenantName) {
          case TenantNames.Anthem:
            return documentTypes?.filter(document => TYPES_ANTHEM_REGENCE.includes(document.documentType));
          case TenantNames.Aetna:
            return documentTypes?.filter(document => TYPES_AETNA.includes(document.documentType));
          case TenantNames.Regence:
            return documentTypes?.filter(document => TYPES_ANTHEM_REGENCE.includes(document.documentType));
          default:
            return [];
        }
    }

    getLineTableConfig(): TableColumn[] {
      return [
        new TableColumn('Service No.', 'sequenceNumber', '80px'),
        new TableColumn('Date Time Created', 'createdOn', '180px'),
        new TableColumn('Date Time Request received', 'dateTimeFaxRecieved', '180px'),
        new TableColumn('Urgency', 'urgency', '120px'),
        new TableColumn('HCPC', 'hcpccode', '100px'),
        new TableColumn('Requested Visits', 'requestedAmount', '110px'),
        new TableColumn('Approved Visits', 'approvedAmount', '100px'),
        new TableColumn('Effective From', 'effectiveFromDate', '180px'),
        new TableColumn('Effective To', 'effectiveToDate', '180px'),
        new TableColumn('Status', 'serviceStatus', '140px'),
        new TableColumn('Decision Date', 'decisionDate', '180px'),
        new TableColumn('Date Urgency Change Downgr', 'urgencyDowngradeDate', '180px'),
        new TableColumn('Date Extension Taken', 'extensionDateTime', '180px'),
        new TableColumn('Status Reason', 'serviceStatusReason', '300px'),
      ];
    }

    mapLineTableData(letterType: string, lineDto: object[], tenantName: TenantNames, authorization: ICoreAuthorizationDTO | null, servicingProvider: IServicingProviderDTO | null, authProviderPgpId: string | null) {
      return lineDto?.filter((line) => {
        switch (letterType)
        {
          case DocumentType.Approval:
            return tenantName === TenantNames.Aetna ? 
              line['serviceStatus'] === ServiceLineStatuses.Approved && line['urgency'] != 'Reopen' :
              line['serviceStatus'] === ServiceLineStatuses.Approved;
          case DocumentType.DenialLetter:
            return line['serviceStatus'] === ServiceLineStatuses.Denied || line['serviceStatus'] === ServiceLineStatuses.PartiallyFavorable;
          case DocumentType.Dismissal:
            return tenantName === TenantNames.Aetna
              ? line['serviceStatus'] === ServiceLineStatuses.Dismissed && line['serviceStatusReason'] !== noAOR
              : line['serviceStatus'] === ServiceLineStatuses.Dismissed;
          case DocumentType.DismissalNoAor:
            return line['serviceStatus'] === ServiceLineStatuses.Dismissed && line['serviceStatusReason'] === noAOR;
          case DocumentType.ExpeditedGrievance:
            return line['urgencyUpgradeDate'] != null || line['extension'] || line['urgencyChangeText'] === 'Downgraded';
          case DocumentType.ReopenedApproval:
            return line['serviceStatus'] === 'Approved' && line['urgency'] === 'Reopen';
          case DocumentType.DecisionTimeframeExtension:
            return line['serviceStatus'] === 'Pending';
          default: false;
        }
      }).map(el => {
        const lineId = el['authorizationDetailId'];

        return {...el, lineId};
      });
    }

    getNoLineMessage(documentType: string, tenantName: TenantNames): string {
      switch (documentType)
      {
        case DocumentType.ReopenedApproval:
          return "NOTE: The authorization has no reopened approval lines to select."
        default:
          return ''
      }
    }

    getAuthorizationProvidersSummary(tenantId: string, authorizationByProductId: string): Observable<object> {
      return of({});
    }
}
